import axios, { AxiosInstance, AxiosError } from 'axios';
import { LoginRequest, RegisterRequest, LoginResponse, ApiResponse } from '../types/auth';

/**
 * Auth Service
 * Handles all authentication-related API calls.
 *
 * @author Civilhealth Development Team
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8082/api';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // If error is 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401 && originalRequest) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          // Store new tokens
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/v1/auth/login', credentials);
    return response.data;
  },

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<ApiResponse<any>> {
    const response = await apiClient.post<ApiResponse<any>>('/v1/auth/register', userData);
    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>('/v1/auth/logout');
    return response.data;
  },

  /**
   * Refresh token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/v1/auth/refresh', { refreshToken });
    return response.data;
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<ApiResponse<any>> {
    const response = await apiClient.get<ApiResponse<any>>('/v1/auth/me');
    return response.data;
  },

  /**
   * Change password
   */
  async changePassword(data: { currentPassword: string; newPassword: string; confirmNewPassword: string }): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>('/v1/auth/change-password', data);
    return response.data;
  },

  /**
   * Forgot password
   */
  async forgotPassword(emailOrMobile: string): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>('/v1/auth/forgot-password', { emailOrMobile });
    return response.data;
  },

  /**
   * Reset password
   */
  async resetPassword(token: string, newPassword: string, confirmNewPassword: string): Promise<ApiResponse<void>> {
    const response = await apiClient.post<ApiResponse<void>>('/v1/auth/reset-password', {
      token,
      newPassword,
      confirmNewPassword,
    });
    return response.data;
  },

  /**
   * Check if username is available
   */
  async checkUsername(username: string): Promise<ApiResponse<boolean>> {
    const response = await apiClient.get<ApiResponse<boolean>>(`/v1/auth/check-username?username=${username}`);
    return response.data;
  },

  /**
   * Check if email is available
   */
  async checkEmail(email: string): Promise<ApiResponse<boolean>> {
    const response = await apiClient.get<ApiResponse<boolean>>(`/v1/auth/check-email?email=${email}`);
    return response.data;
  },

  /**
   * Check if mobile is available
   */
  async checkMobile(mobile: string): Promise<ApiResponse<boolean>> {
    const response = await apiClient.get<ApiResponse<boolean>>(`/v1/auth/check-mobile?mobile=${mobile}`);
    return response.data;
  },
};

export default apiClient;
